import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, ArrowRight, ArrowLeft, Heart, Landmark, Users, Cpu, ShoppingCart, GraduationCap, Scale, Factory, Building, Briefcase, Loader2, Sparkles, ExternalLink, AlertTriangle, MessageSquare, FlaskConical, BookOpen } from "lucide-react";
import { SECTORS, STRUCTURES } from "@/data/staticData";

const iconMap = { Heart, Landmark, Users, Cpu, ShoppingCart, GraduationCap, Scale, Factory, Building, Briefcase };

const OFFICIAL_LINKS = [
  { label: "RGPD — Règlement (UE) 2016/679", url: "https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX%3A32016R0679" },
  { label: "AI Act — Règlement (UE) 2024/1689", url: "https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX%3A32024R1689" },
  { label: "CNIL — Guide Intelligence Artificielle", url: "https://www.cnil.fr/fr/intelligence-artificielle-ia" },
  { label: "EU AI Office", url: "https://digital-strategy.ec.europa.eu/en/policies/european-approach-artificial-intelligence" },
];

const TOTAL_STEPS = 4;

export default function OnboardingPage() {
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sector, setSector] = useState("");
  const [structure, setStructure] = useState("");
  const [customContext, setCustomContext] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFinish = async () => {
    setLoading(true);
    try {
      await API.post("/user/onboarding", { sector, structure, custom_context: customContext || null });
      updateUser({ sector, structure, custom_context: customContext, onboarded: true });
      navigate("/dashboard");
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await API.post("/user/onboarding", { sector: "autre", structure: "pme", custom_context: null });
      updateUser({ sector: "autre", structure: "pme", onboarded: true });
      navigate("/dashboard");
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--ia-surface)] flex items-center justify-center p-4" data-testid="onboarding-page">
      <Card className="w-full max-w-2xl shadow-sm border-[var(--ia-border)] animate-fade-in">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4"><Shield className="h-10 w-10 text-[var(--ia-navy)]" /></div>
          <CardTitle className="text-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {step === 0 ? "Bienvenue sur IA Conforme" : "Personnalisez votre expérience"}
          </CardTitle>
          <CardDescription>Étape {step + 1} sur {TOTAL_STEPS}</CardDescription>
          <Progress value={((step + 1) / TOTAL_STEPS) * 100} className="mt-4 h-1.5" />
        </CardHeader>
        <CardContent className="pt-6">

          {/* Étape 0 : Bienvenue + Présentation + Disclaimer */}
          {step === 0 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <Sparkles className="h-8 w-8 text-[var(--ia-blue)] mx-auto mb-3" />
                <p className="text-sm text-[var(--ia-text-muted)] leading-relaxed max-w-md mx-auto">
                  Votre assistant pédagogique pour comprendre et appliquer le <strong>RGPD</strong> et l'<strong>AI Act</strong>. Découvrez les outils à votre disposition :
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <div className="flex items-start gap-3 p-3 rounded-md border border-[var(--ia-border)] bg-white">
                  <MessageSquare className="h-4 w-4 text-[var(--ia-blue)] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Chat IA expert</p>
                    <p className="text-[11px] text-[var(--ia-muted)]">Posez vos questions sur la conformité IA en langage naturel</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md border border-[var(--ia-border)] bg-white">
                  <FlaskConical className="h-4 w-4 text-[var(--ia-blue)] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Simulateur de risques</p>
                    <p className="text-[11px] text-[var(--ia-muted)]">Analysez vos cas d'usage IA selon l'AI Act</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md border border-[var(--ia-border)] bg-white">
                  <BookOpen className="h-4 w-4 text-[var(--ia-blue)] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Base de connaissances</p>
                    <p className="text-[11px] text-[var(--ia-muted)]">Glossaire, quiz, checklist, études de cas, timeline</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md border border-[var(--ia-border)] bg-white">
                  <Scale className="h-4 w-4 text-[var(--ia-blue)] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Textes officiels</p>
                    <p className="text-[11px] text-[var(--ia-muted)]">Liens directs vers les réglementations en vigueur</p>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <Alert className="border-amber-200 bg-amber-50 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-xs text-amber-700 leading-relaxed">
                  <strong>Avertissement :</strong> Cet outil est à visée <strong>exclusivement pédagogique et informative</strong>. Il ne constitue en aucun cas un conseil juridique au sens de la loi n°71-1130 et ne saurait remplacer la consultation d'un <strong>avocat, DPO ou juriste qualifié</strong>. Les réponses de l'IA peuvent contenir des imprécisions.
                </AlertDescription>
              </Alert>

              {/* Liens officiels */}
              <div className="p-3 rounded-md border border-[var(--ia-border)] bg-slate-50">
                <p className="text-xs font-semibold text-[var(--ia-muted)] mb-2 uppercase tracking-wider flex items-center gap-1.5">
                  <Scale className="h-3 w-3" /> Textes officiels de référence
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {OFFICIAL_LINKS.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" data-testid={`onboarding-link-${i}`}
                      className="flex items-center gap-1.5 text-[11px] text-[var(--ia-blue)] hover:underline">
                      <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Étape 1 : Secteur */}
          {step === 1 && (
            <div className="animate-fade-in">
              <h3 className="text-lg font-semibold mb-4 text-center" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Quel est votre secteur d'activité ?</h3>
              <p className="text-xs text-[var(--ia-muted)] text-center mb-4">Cela nous permet d'adapter les réponses à votre contexte métier.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {SECTORS.map((s) => {
                  const Icon = iconMap[s.icon] || Briefcase;
                  return (
                    <button key={s.id} data-testid={`sector-${s.id}`}
                      onClick={() => setSector(s.id)}
                      className={`p-4 rounded-md border text-left transition-all duration-200 hover:-translate-y-px hover:shadow-sm ${sector === s.id ? "border-[var(--ia-blue)] bg-blue-50 shadow-sm" : "border-[var(--ia-border)] bg-white hover:border-slate-300"}`}>
                      <Icon className={`h-5 w-5 mb-2 ${sector === s.id ? "text-[var(--ia-blue)]" : "text-[var(--ia-muted)]"}`} />
                      <span className="text-sm font-medium">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Étape 2 : Structure */}
          {step === 2 && (
            <div className="animate-fade-in">
              <h3 className="text-lg font-semibold mb-4 text-center" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Quelle est la taille de votre structure ?</h3>
              <p className="text-xs text-[var(--ia-muted)] text-center mb-4">Les obligations varient selon la taille de votre organisation.</p>
              <div className="space-y-3">
                {STRUCTURES.map((s) => (
                  <button key={s.id} data-testid={`structure-${s.id}`}
                    onClick={() => setStructure(s.id)}
                    className={`w-full p-4 rounded-md border text-left transition-all duration-200 ${structure === s.id ? "border-[var(--ia-blue)] bg-blue-50" : "border-[var(--ia-border)] bg-white hover:border-slate-300"}`}>
                    <span className="text-sm font-medium">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Étape 3 : Contexte */}
          {step === 3 && (
            <div className="animate-fade-in">
              <h3 className="text-lg font-semibold mb-4 text-center" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Contexte spécifique (optionnel)</h3>
              <Textarea data-testid="custom-context-input" rows={4} placeholder="Décrivez votre projet IA ou votre contexte spécifique... Par exemple : « Nous utilisons un chatbot pour le service client » ou « Nous développons un outil de scoring RH »" value={customContext} onChange={(e) => setCustomContext(e.target.value)} className="resize-none" />
              <p className="text-xs text-[var(--ia-muted)] mt-2">Ce contexte permettra à l'IA de mieux adapter ses réponses à votre situation.</p>

              {/* Rappel disclaimer */}
              <div className="mt-4 p-2.5 rounded-md bg-slate-50 border border-[var(--ia-border)]">
                <p className="text-[10px] text-[var(--ia-muted)] flex items-start gap-1.5">
                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0 text-amber-500" />
                  Rappel : les réponses fournies sont à visée pédagogique. Pour toute décision juridique, consultez un professionnel qualifié.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            {step > 0 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour
              </Button>
            ) : (
              <Button variant="ghost" onClick={handleSkip} disabled={loading} data-testid="onboarding-skip-btn" className="text-[var(--ia-muted)]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Passer
              </Button>
            )}
            {step < TOTAL_STEPS - 1 ? (
              <Button onClick={() => setStep(step + 1)} disabled={step === 1 ? !sector : step === 2 ? !structure : false} data-testid="onboarding-next-btn" className="bg-[var(--ia-navy)]">
                {step === 0 ? "C'est parti" : "Suivant"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleFinish} disabled={loading} data-testid="onboarding-finish-btn" className="bg-[var(--ia-navy)]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Commencer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
